import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import LogoText from "../components/LogoText";
import { ArrowLeft, ExternalLink, Save, Plus, Trash2, ChevronUp, ChevronDown, Image as ImgIcon, Type, List, Mail as MailIcon, Quote, X, CheckCircle2, Pencil, Upload, Palette, AlignLeft, AlignCenter, AlignRight, Smartphone, Monitor, Youtube, ShoppingBag } from "lucide-react";
import { COLOR_GROUPS, SIZE_LABELS, ALIGN_OPTIONS_DATA, FONT_PAIRS, styleDefault, themeStyleDefault, resolveStyle } from "../lib/funnelStyle";
import Brand from "../components/Brand";
import TutorialVideo from "../components/TutorialVideo";

const BLOCK_TYPES = [
  { type: "heading",    icon: Type,        label: "Headline",      desc: "Big bold title" },
  { type: "paragraph",  icon: Pencil,      label: "Paragraph",     desc: "Body text" },
  { type: "image",      icon: ImgIcon,     label: "Image",         desc: "Photo or graphic" },
  { type: "video",      icon: Youtube,     label: "Video",         desc: "Paste a YouTube link" },
  { type: "benefits",   icon: List,        label: "Benefits List", desc: "Checkmark bullet list" },
  { type: "quote",      icon: Quote,       label: "Testimonial",   desc: "Customer quote box" },
  { type: "product",    icon: ShoppingBag, label: "Buy Button",    desc: "Sell your book, PDF, or course" },
  { type: "form",       icon: MailIcon,    label: "Email Form",    desc: "Capture visitor emails" },
];

/* Extract the 11-char YouTube video ID from any common YouTube URL format.
   Accepts youtu.be/XXX, youtube.com/watch?v=XXX, youtube.com/embed/XXX,
   youtube.com/shorts/XXX, and bare 11-character IDs. Returns null if none found. */
const extractYouTubeId = (url) => {
  if (!url) return null;
  const s = String(url).trim();
  // Bare ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  // Patterns
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m) return m[1];
  }
  return null;
};

const defaultBlock = (type) => {
  const base = { id: crypto.randomUUID(), type, style: styleDefault(type) };
  switch (type) {
    case "heading":   return { ...base, text: "Your bold headline goes here" };
    case "paragraph": return { ...base, text: "A short paragraph that explains what they'll get and why it matters." };
    case "image":     return { ...base, src: "/templates/faith/cross-sunset.jpg", height: 280 };
    case "video":     return { ...base, url: "", videoId: "" };
    case "product":   return { ...base,
      productName: "Your Book / PDF / Course",
      productDescription: "A short line about what they get when they buy.",
      price: 17,
      buyUrl: "",
      cta: "Get It Now",
      imageSrc: "",
    };
    case "benefits":  return { ...base, items: ["Instant access — delivered to your inbox", "Free, no credit card needed", "Share it freely"] };
    case "quote":     return { ...base, text: "This guide changed how I share the Gospel.", author: "— A friend in Christ" };
    case "form":      return { ...base, cta: "Send It to Me", note: "No spam · Unsubscribe anytime", buttonColor: "red" };
    default: return null;
  }
};

/* Resolve a block's style with sensible fallbacks (handles old blocks with no `style` field) */
// resolveStyle is imported from ../lib/funnelStyle

const seedSections = () => [];  // BLANK CANVAS — user clicks the big + Add button to add their first block

export default function FunnelBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [theme, setTheme] = useState({ bg: "/templates/faith/cross-sunset.jpg", bgOpacity: 0.35, style: themeStyleDefault() });
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [imagePicker, setImagePicker] = useState({ open: false, target: null });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop"); // 'desktop' | 'phone'
  const [placingType, setPlacingType] = useState(null); // when set, user is in click-to-place mode for a new block
  const [showAddMenu, setShowAddMenu] = useState(false); // big blue + button popover

  // Snapshot of last-saved state — used to detect unsaved changes & enable Discard
  const savedSnapshot = useRef(null);
  const snapshot = (s = sections, t = theme, n = name) => JSON.stringify({ sections: s, theme: t, name: n });
  const dirty = funnel && savedSnapshot.current !== null && savedSnapshot.current !== snapshot();

  // Browser warning if user tries to close tab with unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = ""; // Chrome requires this
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    (async () => {
      try {
        const [f, tpls] = await Promise.all([
          api.get(`/funnels/${id}/public`),
          api.get("/funnel-templates"),
        ]);
        setFunnel(f.data);
        setName(f.data.name);
        setTemplates(tpls.data.templates);
        const loadedTheme = { bg: f.data.image, bgOpacity: (f.data.theme?.bgOpacity ?? 0.35), style: f.data.theme?.style || themeStyleDefault() };
        const loadedSections = f.data.sections && f.data.sections.length ? f.data.sections : seedSections();
        setTheme(loadedTheme);
        setSections(loadedSections);
        // Mark this loaded state as the "saved snapshot" — anything user edits after this is "dirty"
        savedSnapshot.current = JSON.stringify({ sections: loadedSections, theme: loadedTheme, name: f.data.name });
      } catch (e) { toast.error("Funnel not found"); nav("/dashboard/funnels"); }
    })();
  }, [id, nav]);

  const addBlock = (type, atIdx = null) => {
    const block = defaultBlock(type);
    setSections(prev => {
      if (atIdx === null) return [...prev, block];
      const next = [...prev]; next.splice(atIdx, 0, block); return next;
    });
  };
  const removeBlock = (bid) => setSections(s => s.filter(x => x.id !== bid));
  const moveBlock = (bid, dir) => {
    setSections(prev => {
      const idx = prev.findIndex(x => x.id === bid);
      if (idx < 0) return prev;
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev]; [next[idx], next[target]] = [next[target], next[idx]]; return next;
    });
  };
  const updateBlock = (bid, patch) => setSections(prev => prev.map(b => b.id === bid ? { ...b, ...patch } : b));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/funnels/${id}/content`, {
        name,
        sections,
        image: theme.bg,
        theme: { bgOpacity: theme.bgOpacity, style: theme.style },
      });
      // Update local funnel status so the badge flips to LIVE immediately
      setFunnel(prev => prev ? { ...prev, status: res.data?.status || "published" } : prev);
      // Update saved snapshot so dirty flag clears
      savedSnapshot.current = snapshot();
      toast.success("Saved & published — your funnel is now LIVE!");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const discard = () => {
    if (!dirty) return;
    if (!window.confirm("Throw away all your unsaved changes?\n\nThis returns the funnel to the last-saved version. This cannot be undone.")) return;
    const last = JSON.parse(savedSnapshot.current);
    setSections(last.sections);
    setTheme(last.theme);
    setName(last.name);
    toast.success("Reverted to last saved version");
  };

  // Try-leave guard for the in-app Back button
  const handleBack = (e) => {
    if (dirty) {
      if (!window.confirm("You have unsaved changes. Leave anyway? They will be lost.")) {
        e.preventDefault();
      }
    }
  };

  if (!funnel) return <div className="min-h-screen flex items-center justify-center text-foreground">Loading editor…</div>;

  return (
    <div className="min-h-screen bg-[#0F0E0C] text-foreground" data-testid="funnel-builder">
      {/* TOP BAR */}
      <header className="sticky top-0 z-40 bg-[#0F172A] border-b border-[#F39A1F]/30 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/funnels" onClick={handleBack} className="text-[#FFFFFF]/70 hover:text-white inline-flex items-center gap-2 text-sm" data-testid="builder-back"><ArrowLeft className="w-4 h-4"/>Back</Link>
          <div className="text-[#FFFFFF]/30">·</div>
          <input value={name} onChange={e=>setName(e.target.value)} className="bg-transparent text-white text-base font-semibold border-b border-transparent hover:border-[#F39A1F]/50 focus:border-[#F39A1F] focus:outline-none px-1" placeholder="Funnel name" data-testid="builder-name"/>
          {funnel.status === "published" ? (
            <span className="inline-flex items-center gap-1 bg-green-600/20 text-green-300 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider-cap border border-green-500/40" data-testid="builder-status">● LIVE</span>
          ) : (
            <span className="inline-flex items-center gap-1 bg-[#F39A1F]/15 text-[#F39A1F] px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider-cap border border-[#F39A1F]/40" data-testid="builder-status">○ DRAFT — hidden from public</span>
          )}
          {dirty && (
            <span className="inline-flex items-center gap-1.5 bg-red-600/15 text-red-300 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider-cap border border-red-500/40 animate-pulse" data-testid="builder-dirty">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400"/> Unsaved Changes
            </span>
          )}
          <span className="text-[10px] tracking-wider-cap text-[#F39A1F]/80 hidden md:inline">Click anything to edit</span>
        </div>
        <div className="flex items-center gap-2">
          {/* PHONE / DESKTOP PREVIEW TOGGLE */}
          <div className="hidden md:inline-flex items-center bg-[#0F0E0C] rounded-md border border-[#F39A1F]/30 p-0.5 mr-2" data-testid="preview-mode-toggle">
            <button
              onClick={() => setPreviewMode("desktop")}
              className={`px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 text-[10px] tracking-wider-cap font-bold uppercase transition ${previewMode === "desktop" ? "bg-[#F39A1F] text-[#0F172A]" : "text-[#FFFFFF]/70 hover:text-white"}`}
              data-testid="preview-mode-desktop"
              title="Desktop preview"
            >
              <Monitor className="w-3.5 h-3.5"/> Desktop
            </button>
            <button
              onClick={() => setPreviewMode("phone")}
              className={`px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 text-[10px] tracking-wider-cap font-bold uppercase transition ${previewMode === "phone" ? "bg-[#F39A1F] text-[#0F172A]" : "text-[#FFFFFF]/70 hover:text-white"}`}
              data-testid="preview-mode-phone"
              title="Phone preview"
            >
              <Smartphone className="w-3.5 h-3.5"/> Phone
            </button>
          </div>
          <a href={`/f/${id}`} target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF]/80 hover:text-white inline-flex items-center gap-2 text-xs px-3 py-2 rounded border border-white/15" data-testid="builder-view-live"><ExternalLink className="w-3 h-3"/>View Live</a>
          {dirty && (
            <button onClick={discard} className="text-[#FFFFFF]/80 hover:text-white inline-flex items-center gap-2 text-xs px-3 py-2 rounded border border-white/15 hover:bg-white/5" data-testid="builder-discard" title="Throw away unsaved changes">
              <Trash2 className="w-3 h-3"/>Discard
            </button>
          )}
          <button onClick={save} disabled={saving || !dirty} className={`text-white px-5 py-2 rounded-md inline-flex items-center gap-2 text-sm font-bold transition disabled:opacity-50 ${dirty ? "bg-red-wine hover:bg-red-wine/90 ring-2 ring-red-500/40 ring-offset-2 ring-offset-[#0F172A]" : "bg-red-wine/40 cursor-not-allowed"}`} data-testid="builder-save"><Save className="w-4 h-4"/>{saving ? "Saving…" : (dirty ? "Save & Publish" : "All Saved")}</button>
        </div>
      </header>

      {/* MIKE'S WALK-THROUGH VIDEO — drop in /api/uploads/tutorials/funnel-builder.mp4
          (recorded on ScreenPal) and it auto-appears. Until then, friendly "coming soon" card. */}
      <TutorialVideo
        pageKey="funnel-builder"
        title="Watch Me Build Your First Funnel."
        subtitle="3 minutes. Just click where I point. That easy."
        src={null}
      />

      <div className="grid lg:grid-cols-[280px_1fr] min-h-[calc(100vh-58px)]">
        {/* LEFT SIDEBAR — WebStarts-style */}
        <aside className="bg-[#0F172A] border-r border-[#F39A1F]/20 p-5 space-y-5">
          {/* BIG BLUE + ADD BUTTON */}
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(o => !o)}
              className="w-full flex items-center gap-3 bg-[#1E90FF] hover:bg-[#1880E8] text-white rounded-full px-4 py-3 shadow-lg transition"
              data-testid="add-button"
            >
              <span className="w-9 h-9 rounded-full bg-white text-[#1E90FF] flex items-center justify-center shadow"><Plus className="w-5 h-5" strokeWidth={3}/></span>
              <span className="text-sm font-bold uppercase tracking-wider-cap">Add</span>
              <ChevronDown className="w-4 h-4 ml-auto opacity-80"/>
            </button>
            {showAddMenu && (
              <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-[#F39A1F] p-2 z-30 space-y-1" onClick={e=>e.stopPropagation()}>
                {BLOCK_TYPES.map(b => (
                  <button
                    key={b.type}
                    onClick={() => { setPlacingType(b.type); setShowAddMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white rounded-md text-left transition"
                    data-testid={`add-pick-${b.type}`}
                  >
                    <b.icon className="w-5 h-5 text-gold-deep flex-shrink-0"/>
                    <div>
                      <div className="text-sm font-bold uppercase tracking-wider-cap text-foreground">{b.label}</div>
                      <div className="text-[11px] text-foreground">{b.desc || ""}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* HERO IMAGE */}
          <div>
            <div className="text-[11px] tracking-wider-cap text-[#F39A1F] font-bold mb-1">★ Hero Image</div>
            <div className="text-[10px] text-[#FFFFFF]/70 mb-2">This big photo shows at the <strong className="text-[#F39A1F]">TOP</strong> of your funnel page.</div>
            <button onClick={() => setImagePicker({ open: true, target: "bg" })} className="block w-full rounded-md overflow-hidden border-2 border-[#F39A1F] hover:border-white transition shadow-lg" data-testid="builder-bg-pick">
              <img src={theme.bg} className="w-full h-24 object-cover"/>
              <div className="bg-[#F39A1F] text-[#0F172A] text-[10px] tracking-wider-cap font-bold uppercase py-1.5 text-center">Click to Change Hero Photo</div>
            </button>
          </div>

          {/* SITE STYLES — set fonts/sizes/colors ONCE, applied everywhere */}
          <div className="pt-4 border-t border-white/10">
            <div className="text-[11px] tracking-wider-cap text-[#F39A1F] font-bold mb-2">★ Site Styles</div>
            <div className="text-[10px] text-[#FFFFFF]/70 mb-3">Set your fonts &amp; colors <strong className="text-[#F39A1F]">ONCE</strong>. They apply to every block.</div>

            {/* FONT PAIRINGS */}
            <div className="text-[10px] tracking-wider-cap text-[#FFFFFF]/80 font-bold mb-1.5">Font Pairing</div>
            <div className="space-y-1.5 mb-4">
              {FONT_PAIRS.map(p => {
                const active = theme.style.fontPair === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setTheme(prev => ({ ...prev, style: { ...prev.style, fontPair: p.id } }))}
                    className={`w-full text-left rounded-md p-2.5 transition border ${active ? "bg-[#F39A1F]/15 border-[#F39A1F]" : "bg-[#0F0E0C] border-[#FFFFFF]/10 hover:border-[#F39A1F]/40"}`}
                    data-testid={`font-pair-${p.id}`}
                  >
                    <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">{p.name}</div>
                    <div style={{ fontFamily: p.heading }} className="text-base text-white leading-tight">{p.preview}</div>
                    <div style={{ fontFamily: p.body }} className="text-[11px] text-[#FFFFFF]/70 mt-0.5">{p.body.split(",")[0].replace(/'/g, "")}</div>
                  </button>
                );
              })}
            </div>

            {/* HEADING COLOR */}
            <div className="text-[10px] tracking-wider-cap text-[#FFFFFF]/80 font-bold mb-1.5">Heading Color</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {COLOR_GROUPS.flatMap(g => g.colors).slice(0, 12).map(c => (
                <button
                  key={c.id}
                  onClick={() => setTheme(prev => ({ ...prev, style: { ...prev.style, headingColor: c.id } }))}
                  title={c.label}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${theme.style.headingColor === c.id ? "border-white scale-110 ring-2 ring-[#F39A1F]" : "border-[#FFFFFF]/20"}`}
                  style={{ background: c.hex }}
                  data-testid={`heading-color-${c.id}`}
                />
              ))}
            </div>

            {/* BODY COLOR */}
            <div className="text-[10px] tracking-wider-cap text-[#FFFFFF]/80 font-bold mb-1.5">Body Text Color</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {COLOR_GROUPS.flatMap(g => g.colors).slice(12, 24).map(c => (
                <button
                  key={c.id}
                  onClick={() => setTheme(prev => ({ ...prev, style: { ...prev.style, bodyColor: c.id } }))}
                  title={c.label}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${theme.style.bodyColor === c.id ? "border-white scale-110 ring-2 ring-[#F39A1F]" : "border-[#FFFFFF]/20"}`}
                  style={{ background: c.hex }}
                  data-testid={`body-color-${c.id}`}
                />
              ))}
            </div>

            {/* HEADING SIZE */}
            <div className="text-[10px] tracking-wider-cap text-[#FFFFFF]/80 font-bold mb-1.5">Heading Size</div>
            <div className="grid grid-cols-5 gap-1">
              {SIZE_LABELS.map(o => (
                <button
                  key={o.id}
                  onClick={() => setTheme(prev => ({ ...prev, style: { ...prev.style, headingSize: o.id } }))}
                  className={`px-1.5 py-1.5 text-[10px] tracking-wider-cap font-bold rounded ${theme.style.headingSize === o.id ? "bg-[#F39A1F] text-[#0F172A]" : "bg-[#0F0E0C] text-[#FFFFFF] hover:bg-[#2A2521]"}`}
                  data-testid={`heading-size-${o.id}`}
                >{o.label}</button>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="text-[10px] tracking-wider-cap text-[#FFFFFF]/60 mb-2">How it works</div>
            <ul className="text-[11px] text-[#FFFFFF]/80 space-y-1.5 list-disc pl-4">
              <li>Click the big blue <strong className="text-[#1E90FF]">+ Add</strong> button to pick what to add</li>
              <li>Then click <strong className="text-[#F39A1F]">where on the page</strong> you want it to land</li>
              <li>Click any text → edit it inline</li>
              <li>Click the hero photo → swap it</li>
              <li>↑ ↓ buttons reorder · 🗑 deletes</li>
              <li>Hit <strong className="text-[#F39A1F]">Save & Publish</strong> when ready</li>
            </ul>
          </div>
        </aside>

        {/* CANVAS */}
        <main className={`relative overflow-y-auto ${previewMode === "phone" ? "bg-[#0F0E0C] py-8" : "bg-ivory"}`}>
          {previewMode === "phone" && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-1.5 bg-[#0F172A] text-[#F39A1F] text-[10px] tracking-wider-cap font-bold uppercase px-3 py-1.5 rounded-full border border-[#F39A1F]/40">
                <Smartphone className="w-3 h-3"/> Phone Preview · 390px wide
              </div>
            </div>
          )}
          <div className={previewMode === "phone" ? "max-w-[390px] mx-auto bg-ivory rounded-[36px] shadow-2xl ring-8 ring-[#0F172A] overflow-hidden" : ""}>
            {/* HERO IMAGE — always at the top of the funnel */}
            <div className="w-full bg-onyx" data-testid="builder-hero-preview">
              <button onClick={() => setImagePicker({ open: true, target: "bg" })} className="block w-full max-w-3xl mx-auto relative group/hero" title="Click to change hero photo">
                <img src={theme.bg} className={`w-full object-cover ${previewMode === "phone" ? "h-[200px]" : "h-[280px] md:h-[360px]"}`}/>
                <div className="absolute top-3 right-3 bg-[#0F172A]/90 text-white text-[10px] tracking-wider-cap font-bold uppercase px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 shadow-lg group-hover/hero:bg-red-wine transition">
                  <ImgIcon className="w-3 h-3"/>{previewMode === "phone" ? "Tap" : "Click"} to change hero
                </div>
              </button>
            </div>

            <div className={`mx-auto space-y-2 ${previewMode === "phone" ? "max-w-full px-5 py-8" : "max-w-3xl px-6 md:px-10 py-12"}`}>
              {/* Top brand bar (just preview) */}
              <div className="flex items-center justify-between mb-8 opacity-60">
                <LogoText size="sm" as="span" />
                <span className="text-[10px] tracking-wider-cap text-gold-deep">A <Brand/> Funnel</span>
              </div>

              {/* PLACEMENT MODE BANNER */}
              {placingType && (
                <div className="sticky top-2 z-20 mb-4 bg-[#1E90FF] text-white rounded-xl shadow-2xl p-3 flex items-center justify-between gap-3 animate-pulse" data-testid="placing-banner">
                  <div className="flex items-center gap-2">
                    <Plus className="w-5 h-5"/>
                    <span className="text-sm font-bold uppercase tracking-wider-cap">
                      Click anywhere below to drop your <strong>{BLOCK_TYPES.find(t=>t.type===placingType)?.label}</strong>
                    </span>
                  </div>
                  <button onClick={() => setPlacingType(null)} className="bg-white text-[#1E90FF] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider-cap hover:bg-white/90" data-testid="placing-cancel">Cancel</button>
                </div>
              )}

              {/* DROP ZONE — top of page (only visible in placement mode) */}
              <DropZone show={!!placingType} onDrop={() => { addBlock(placingType, 0); setPlacingType(null); }} idx={0} />

              {sections.map((b, idx) => (
                <div key={b.id}>
                  <BlockRenderer
                    block={b}
                    index={idx}
                    total={sections.length}
                    themeStyle={theme.style}
                    onChange={patch => updateBlock(b.id, patch)}
                    onRemove={() => removeBlock(b.id)}
                    onMove={dir => moveBlock(b.id, dir)}
                    onPickImage={() => setImagePicker({ open: true, target: b.id })}
                  />
                  {/* DROP ZONE between blocks */}
                  <DropZone show={!!placingType} onDrop={() => { addBlock(placingType, idx + 1); setPlacingType(null); }} idx={idx + 1} />
                </div>
              ))}

              {/* EMPTY-CANVAS HINT */}
              {sections.length === 0 && !placingType && (
                <div className="text-center py-20 px-6 border-2 border-dashed border-[#F39A1F]/40 rounded-xl">
                  <div className="text-[11px] tracking-wider-cap text-gold-deep font-bold mb-2">Blank Page</div>
                  <h3 className="font-serif-display text-3xl text-red-wine uppercase mb-3">Your Funnel Starts Here</h3>
                  <p className="text-foreground max-w-md mx-auto">Click the big blue <strong className="text-[#1E90FF]">+ Add</strong> button on the left to drop your first block — a headline, image, paragraph, or email form. Your hero photo is already up top ↑</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* IMAGE PICKER MODAL */}
      {imagePicker.open && (
        <ImagePickerModal
          templates={templates}
          onClose={() => setImagePicker({ open: false, target: null })}
          onPick={(url, picked) => {
            // Set the image
            if (imagePicker.target === "bg") setTheme(prev => ({...prev, bg: url}));
            else updateBlock(imagePicker.target, { src: url });

            // ★ If they picked a Faitharama Product → auto-insert a paired CTA below it (with their affiliate link)
            if (picked && picked.category === "product" && picked.cta_text && imagePicker.target !== "bg") {
              const refCode = funnel?.user_affiliate_code || "";
              const ctaUrl = `${picked.cta_path}${picked.cta_path.includes("?") ? "&" : "?"}ref=${encodeURIComponent(refCode || "")}`;
              const newCta = {
                id: crypto.randomUUID(),
                type: "form",
                style: styleDefault("form"),
                cta: picked.cta_text,
                note: `★ ${picked.name}`,
                buttonColor: "red",
                external_url: ctaUrl,  // public funnel reads this and routes to URL instead of capturing email
              };
              const idx = sections.findIndex(s => s.id === imagePicker.target);
              if (idx >= 0) {
                setSections(prev => [...prev.slice(0, idx + 1), newCta, ...prev.slice(idx + 1)]);
                toast.success("✓ Image + Buy/Form auto-paired below — your affiliate code is baked in.");
              }
            }
            setImagePicker({ open: false, target: null });
          }}
        />
      )}
    </div>
  );
}

/* ============== STYLE POPOVER ============== */
function StylePopover({ block, onChange, supportSize = true }) {
  const [open, setOpen] = useState(false);
  const cur = block.style || styleDefault(block.type);
  const update = (patch) => onChange({ style: { ...cur, ...patch } });
  const ALIGN_ICONS = { left: AlignLeft, center: AlignCenter, right: AlignRight };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o=>!o)} className="text-white rounded p-1 hover:bg-red-wine inline-flex items-center gap-1" title="Text style" data-testid={`style-toggle-${block.id}`}>
        <Palette className="w-3.5 h-3.5"/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 bg-white text-foreground rounded-xl shadow-2xl border-2 border-[#F39A1F] p-4 w-[320px] max-h-[60vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
          {supportSize && (
            <div className="mb-4">
              <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-1.5">Text Size</div>
              <div className="grid grid-cols-5 gap-1">
                {SIZE_LABELS.map(o => (
                  <button key={o.id} onClick={() => update({ size: o.id })} className={`px-1.5 py-1.5 text-[10px] tracking-wider-cap font-bold rounded ${cur.size===o.id ? "bg-red-wine text-white" : "bg-white hover:bg-white"}`} data-testid={`style-size-${block.id}-${o.id}`}>{o.label}</button>
                ))}
              </div>
            </div>
          )}
          <div className="mb-4">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-2">Colors <span className="text-foreground/50 font-normal">· {COLOR_GROUPS.reduce((n,g)=>n+g.colors.length,0)} shades</span></div>
            {COLOR_GROUPS.map(group => (
              <div key={group.name} className="mb-2.5 last:mb-0">
                <div className="text-[9px] tracking-wider-cap font-bold text-foreground mb-1">{group.name}</div>
                <div className="flex flex-wrap gap-1.5">
                  {group.colors.map(c => (
                    <button
                      key={c.id}
                      onClick={() => update({ color: c.id })}
                      title={c.label}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${cur.color===c.id ? "border-red-wine ring-2 ring-red-wine/40 scale-110" : "border-foreground/15"}`}
                      style={{ background: c.hex }}
                      data-testid={`style-color-${block.id}-${c.id}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-1.5">Alignment</div>
            <div className="grid grid-cols-3 gap-1">
              {ALIGN_OPTIONS_DATA.map(a => {
                const Ico = ALIGN_ICONS[a.id];
                return (
                  <button key={a.id} onClick={() => update({ align: a.id })} className={`py-1.5 rounded inline-flex items-center justify-center ${cur.align===a.id ? "bg-red-wine text-white" : "bg-white hover:bg-white"}`} data-testid={`style-align-${block.id}-${a.id}`}>
                    <Ico className="w-3.5 h-3.5"/>
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={()=>setOpen(false)} className="mt-3 text-[10px] tracking-wider-cap font-bold uppercase text-foreground hover:text-red-wine">Close</button>
        </div>
      )}
    </div>
  );
}

/* ============== DROP ZONE — only active in placement mode ============== */
function DropZone({ show, onDrop, idx }) {
  if (!show) return null;
  return (
    <button
      onClick={onDrop}
      className="block w-full my-1 group/drop"
      data-testid={`dropzone-${idx}`}
    >
      <div className="h-12 rounded-md border-2 border-dashed border-[#1E90FF]/40 bg-[#1E90FF]/5 group-hover/drop:border-[#1E90FF] group-hover/drop:bg-[#1E90FF]/15 group-hover/drop:h-16 transition-all flex items-center justify-center">
        <div className="text-[#1E90FF] text-[11px] tracking-wider-cap font-bold uppercase opacity-60 group-hover/drop:opacity-100 transition inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> Drop Here
        </div>
      </div>
    </button>
  );
}

/* ============== INSERT-HERE DIVIDER ============== */
function InsertHere({ idx, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative h-3 group/insert" data-testid={`insert-here-${idx}`}>
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover/insert:opacity-100 transition pointer-events-none">
        <div className="h-px flex-1 bg-[#F39A1F]/40"/>
        <button onClick={() => setOpen(o=>!o)} className="pointer-events-auto bg-red-wine text-white rounded-full px-3 py-1 text-[10px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-1 mx-2 shadow-md hover:bg-red-wine/90" data-testid={`insert-here-btn-${idx}`}>
          <Plus className="w-3 h-3"/> Insert Block Here
        </button>
        <div className="h-px flex-1 bg-[#F39A1F]/40"/>
      </div>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-30 bg-white rounded-lg shadow-2xl border-2 border-[#F39A1F] p-2 flex flex-wrap gap-1.5 w-[320px] justify-center">
          {BLOCK_TYPES.map(b => (
            <button key={b.type} onClick={() => { onPick(b.type); setOpen(false); }} className="text-[10px] tracking-wider-cap font-bold uppercase px-2.5 py-1.5 rounded-full bg-white border border-[#F39A1F]/40 hover:bg-white inline-flex items-center gap-1 transition" data-testid={`insert-pick-${idx}-${b.type}`}>
              <b.icon className="w-3 h-3 text-gold-deep"/>{b.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============== BLOCK RENDERER ============== */
function BlockRenderer({ block, index, total, themeStyle, onChange, onRemove, onMove, onPickImage }) {
  const [hover, setHover] = useState(false);
  const showStyle = block.type !== "image" && block.type !== "form" && block.type !== "video" && block.type !== "product";
  return (
    <div className="relative group" onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)} data-testid={`block-${block.type}-${index}`}>
      {/* Hover toolbar — top-right INSIDE the block (never clips) */}
      <div className={`absolute right-2 top-2 z-20 transition ${hover?'opacity-100':'opacity-0 pointer-events-none'} flex items-center gap-1 bg-[#0F172A]/95 rounded-md px-1 py-1 shadow-lg`}>
        {showStyle && <><StylePopover block={block} onChange={onChange}/><div className="w-px h-4 bg-white/20"/></>}
        <button onClick={()=>onMove("up")} disabled={index===0} className="text-white rounded p-1 hover:bg-red-wine disabled:opacity-30" title="Move up" data-testid={`move-up-${index}`}><ChevronUp className="w-3.5 h-3.5"/></button>
        <button onClick={()=>onMove("down")} disabled={index===total-1} className="text-white rounded p-1 hover:bg-red-wine disabled:opacity-30" title="Move down" data-testid={`move-down-${index}`}><ChevronDown className="w-3.5 h-3.5"/></button>
        <div className="w-px h-4 bg-white/20"/>
        <button onClick={onRemove} className="text-white rounded p-1 hover:bg-red-wine" title="Delete block" data-testid={`remove-${index}`}><Trash2 className="w-3.5 h-3.5"/></button>
      </div>
      <div className={`rounded-lg ${hover?'ring-2 ring-[#F39A1F]/60 ring-offset-2 ring-offset-ivory':''} transition`}>
        {block.type === "heading"   && <HeadingBlock block={block} themeStyle={themeStyle} onChange={onChange}/>}
        {block.type === "paragraph" && <ParagraphBlock block={block} themeStyle={themeStyle} onChange={onChange}/>}
        {block.type === "image"     && <ImageBlock block={block} onPickImage={onPickImage} onChange={onChange}/>}
        {block.type === "video"     && <VideoBlock block={block} onChange={onChange}/>}
        {block.type === "product"   && <ProductBlock block={block} onChange={onChange}/>}
        {block.type === "benefits"  && <BenefitsBlock block={block} themeStyle={themeStyle} onChange={onChange}/>}
        {block.type === "quote"     && <QuoteBlock block={block} themeStyle={themeStyle} onChange={onChange}/>}
        {block.type === "form"      && <FormBlock block={block} onChange={onChange}/>}
      </div>
    </div>
  );
}

const Editable = ({ value, onChange, className, multiline = false, placeholder, testid, style }) => {
  const ref = useRef(null);
  return multiline ? (
    <textarea
      ref={ref}
      value={value}
      onChange={e=>onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      rows={2}
      style={style}
      className={`${className} bg-transparent border-2 border-transparent hover:border-dashed hover:border-[#F39A1F]/60 focus:border-[#F39A1F] focus:outline-none rounded px-2 py-1 w-full resize-none`}
    />
  ) : (
    <input
      ref={ref}
      value={value}
      onChange={e=>onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      style={style}
      className={`${className} bg-transparent border-2 border-transparent hover:border-dashed hover:border-[#F39A1F]/60 focus:border-[#F39A1F] focus:outline-none rounded px-2 py-1 w-full`}
    />
  );
};

const HeadingBlock = ({ block, themeStyle, onChange }) => {
  const { sizeCls, colorHex, alignCls, fontFamily } = resolveStyle(block, themeStyle);
  return (
    <div className="py-3">
      <Editable value={block.text} onChange={t=>onChange({text:t})} placeholder="Headline" testid={`edit-heading-${block.id}`}
        className={`${sizeCls} ${alignCls} uppercase leading-[1.05]`} style={{ color: colorHex, fontFamily }}/>
      <div className={`h-px w-16 bg-[#F39A1F] mt-3 ${alignCls === "text-center" ? "mx-auto" : alignCls === "text-right" ? "ml-auto mr-2" : "mx-2"}`}/>
    </div>
  );
};

const ParagraphBlock = ({ block, themeStyle, onChange }) => {
  const { sizeCls, colorHex, alignCls, fontFamily } = resolveStyle(block, themeStyle);
  return (
    <div className="py-3">
      <Editable multiline value={block.text} onChange={t=>onChange({text:t})} placeholder="Paragraph text…" testid={`edit-paragraph-${block.id}`}
        className={`${sizeCls} ${alignCls}`} style={{ color: colorHex, fontFamily }}/>
    </div>
  );
};

/* Shared image upload helper — POSTs the picked file to /api/uploads/image
   and returns the server URL string. Used by ImageBlock and ProductBlock. */
const uploadImageFile = async (file) => {
  if (!file) return null;
  if (!file.type?.startsWith("image/")) {
    toast.error("That doesn't look like an image. Try a .jpg, .png, or .webp.");
    return null;
  }
  if (file.size > 8 * 1024 * 1024) {
    toast.error("That image is over 8 MB — try a smaller one or save it as a JPG.");
    return null;
  }
  try {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data?.url || null;
  } catch (e) {
    toast.error("Upload failed. Try again in a moment.");
    return null;
  }
};

const ImageBlock = ({ block, onPickImage, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadImageFile(file);
    setUploading(false);
    if (url) onChange?.({ src: url });
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="py-3">
      <button onClick={onPickImage} className="block w-full relative rounded-xl overflow-hidden border-2 border-dashed border-[#F39A1F]/0 hover:border-[#F39A1F]/60 transition" data-testid={`edit-image-${block.id}`}>
        <img src={block.src} className="w-full object-cover" style={{ height: `${block.height}px` }}/>
        <div className="absolute top-3 right-3 bg-[#0F172A]/85 text-white text-[10px] tracking-wider-cap font-bold uppercase px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
          <ImgIcon className="w-3 h-3"/>Pick from library
        </div>
      </button>
      <div className="mt-2 flex gap-2">
        <button onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-xs uppercase tracking-wider font-bold border-2 hover:bg-white transition disabled:opacity-60"
                style={{ borderColor: "#2E9F3A", color: "#9C6B14" }}
                data-testid={`upload-image-${block.id}`}>
          <Upload className="w-3.5 h-3.5"/>
          {uploading ? "Uploading…" : "Upload From My Computer"}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
};

/* VIDEO BLOCK — paste a YouTube link, get a 16:9 embedded player. */
const VideoBlock = ({ block, onChange }) => {
  const vid = block.videoId || extractYouTubeId(block.url);
  return (
    <div className="py-3">
      {vid ? (
        <div className="relative rounded-xl overflow-hidden bg-black shadow-md" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            src={`https://www.youtube.com/embed/${vid}?rel=0&modestbranding=1`}
            title="Funnel video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid={`video-embed-${block.id}`}
          />
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-[#F39A1F]/60 bg-white p-6 text-center">
          <Youtube className="w-10 h-10 text-red-wine mx-auto mb-2"/>
          <div className="text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-3">Paste any YouTube link below</div>
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <Youtube className="w-4 h-4 text-red-wine flex-shrink-0"/>
        <input
          type="text"
          value={block.url || ""}
          onChange={(e) => {
            const url = e.target.value;
            onChange({ url, videoId: extractYouTubeId(url) || "" });
          }}
          placeholder="https://www.youtube.com/watch?v=…"
          className="flex-1 text-sm bg-white border border-foreground/15 rounded-md px-3 py-2 focus:outline-none focus:border-[#F39A1F]"
          data-testid={`edit-video-url-${block.id}`}
        />
      </div>
      {!vid && block.url && (
        <div className="mt-1 text-[11px] text-red-wine">That doesn't look like a YouTube link. Try one that ends in <code>youtu.be/XXXXX</code> or <code>watch?v=XXXXX</code>.</div>
      )}
    </div>
  );
};

/* PRODUCT / BUY BUTTON BLOCK — sell your book, PDF, course, anything.
   Member uploads a product image, types a name + price, pastes a checkout
   URL (Stripe, Gumroad, PayPal). Renders as a polished buy card on the
   published funnel. */
const ProductBlock = ({ block, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadImageFile(file);
    setUploading(false);
    if (url) onChange({ imageSrc: url });
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="py-3">
      <div className="rounded-xl border-2 p-5 md:p-6"
           style={{ background: "#FFFFFF", borderColor: "#2E9F3A" }}>
        <div className="grid md:grid-cols-[140px_1fr_auto] gap-5 items-start">
          {/* Product image */}
          <div className="flex flex-col items-stretch">
            {block.imageSrc ? (
              <img src={block.imageSrc} alt="" className="w-full aspect-square object-cover rounded-lg border" style={{ borderColor: "rgba(201,162,39,0.4)" }}/>
            ) : (
              <div className="w-full aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-foreground/50"
                   style={{ borderColor: "rgba(201,162,39,0.6)", background: "#EAF6FC" }}>
                <ShoppingBag className="w-7 h-7"/>
                <div className="text-[10px] uppercase tracking-wider-cap font-bold text-center px-2">Product cover</div>
              </div>
            )}
            <button onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="mt-2 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[10px] uppercase tracking-wider-cap font-bold border-2 hover:bg-white transition disabled:opacity-60"
                    style={{ borderColor: "#2E9F3A", color: "#9C6B14" }}
                    data-testid={`upload-product-image-${block.id}`}>
              <Upload className="w-3 h-3"/>
              {uploading ? "Uploading…" : (block.imageSrc ? "Replace" : "Upload Cover")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden"/>
          </div>

          {/* Name + description */}
          <div>
            <input
              type="text"
              value={block.productName || ""}
              onChange={(e) => onChange({ productName: e.target.value })}
              placeholder="Product name (your book, PDF, course)"
              className="w-full font-bold text-2xl bg-transparent border-b border-foreground/15 focus:border-[#2E9F3A] focus:outline-none pb-1 text-red-wine"
              data-testid={`edit-product-name-${block.id}`}
            />
            <textarea
              value={block.productDescription || ""}
              onChange={(e) => onChange({ productDescription: e.target.value })}
              placeholder="A short line about what they get when they buy."
              rows={2}
              className="w-full mt-3 text-base bg-transparent border border-foreground/15 rounded-md px-3 py-2 focus:border-[#2E9F3A] focus:outline-none resize-none"
              data-testid={`edit-product-desc-${block.id}`}
            />
          </div>

          {/* Price + CTA preview */}
          <div className="text-center">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-foreground/60">Price (USD)</div>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-3xl font-bold text-red-wine">$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={block.price ?? 0}
                onChange={(e) => onChange({ price: Number(e.target.value) || 0 })}
                className="w-20 text-4xl font-bold bg-transparent border-b-2 border-[#2E9F3A] focus:outline-none text-red-wine text-center"
                data-testid={`edit-product-price-${block.id}`}
              />
            </div>
            <div className="mt-3 inline-flex items-center gap-2 px-5 py-3 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-md text-white"
                 style={{ background: "#C9202E" }}>
              <ShoppingBag className="w-4 h-4"/>{block.cta || "Get It Now"}
            </div>
          </div>
        </div>

        {/* Checkout URL + button text */}
        <div className="mt-5 border-t border-foreground/10 pt-4 space-y-2">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-foreground/60">Checkout link (Stripe, Gumroad, PayPal — wherever you take the money)</div>
          <input
            type="url"
            value={block.buyUrl || ""}
            onChange={(e) => onChange({ buyUrl: e.target.value })}
            placeholder="https://buy.stripe.com/...  ·  https://gumroad.com/l/...  ·  https://paypal.me/..."
            className="w-full text-sm bg-white border border-foreground/15 rounded-md px-3 py-2 focus:border-[#2E9F3A] focus:outline-none font-mono"
            data-testid={`edit-product-url-${block.id}`}
          />
          <input
            type="text"
            value={block.cta || ""}
            onChange={(e) => onChange({ cta: e.target.value })}
            placeholder="Button text — Get It Now / Buy The Book / Send Me The PDF"
            className="w-full text-sm bg-white border border-foreground/15 rounded-md px-3 py-2 focus:border-[#2E9F3A] focus:outline-none"
            data-testid={`edit-product-cta-${block.id}`}
          />
          {!block.buyUrl && (
            <div className="text-[11px] text-[#C9202E] flex items-start gap-1.5">
              <span>⚠</span>
              <span>Add your checkout link or the buy button won't work. Don't have one yet? Set one up free at <strong>gumroad.com</strong> or use your <strong>Stripe payment link</strong>.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BenefitsBlock = ({ block, themeStyle, onChange }) => {
  const { sizeCls, colorHex, alignCls, fontFamily } = resolveStyle(block, themeStyle);
  return (
    <div className="py-3 space-y-1.5">
      {block.items.map((it, i) => (
        <div key={i} className={`flex items-start gap-2 group/item ${alignCls === "text-center" ? "justify-center" : alignCls === "text-right" ? "justify-end" : ""}`}>
          <CheckCircle2 className="w-5 h-5 text-gold-deep mt-1 flex-shrink-0"/>
          <Editable value={it} onChange={t=>{const next=[...block.items]; next[i]=t; onChange({items:next});}} placeholder={`Benefit ${i+1}`} testid={`edit-benefit-${block.id}-${i}`}
            className={`${sizeCls} flex-1`} style={{ color: colorHex, fontFamily }}/>
          {block.items.length > 1 && (
            <button onClick={()=>onChange({items: block.items.filter((_,j)=>j!==i)})} className="opacity-0 group-hover/item:opacity-100 text-red-wine/60 hover:text-red-wine"><X className="w-4 h-4"/></button>
          )}
        </div>
      ))}
      <button onClick={()=>onChange({items: [...block.items, "New benefit"]})} className="text-[10px] tracking-wider-cap text-gold-deep font-bold uppercase mt-2 inline-flex items-center gap-1"><Plus className="w-3 h-3"/>Add benefit</button>
    </div>
  );
};

const QuoteBlock = ({ block, themeStyle, onChange }) => {
  const { sizeCls, colorHex, alignCls, fontFamily } = resolveStyle(block, themeStyle);
  return (
    <div className="py-5 px-6 bg-white/70 border-l-4 border-[#F39A1F] my-3 rounded-r-lg">
      <Quote className="w-6 h-6 text-gold-deep mb-2"/>
      <Editable multiline value={block.text} onChange={t=>onChange({text:t})} placeholder="Testimonial quote…" testid={`edit-quote-text-${block.id}`}
        className={`${sizeCls} ${alignCls} italic`} style={{ color: colorHex, fontFamily }}/>
      <Editable value={block.author} onChange={t=>onChange({author:t})} placeholder="— Author" testid={`edit-quote-author-${block.id}`}
        className={`text-sm text-foreground mt-2 not-italic ${alignCls}`} style={{ fontFamily }}/>
    </div>
  );
};

const FormBlock = ({ block, onChange }) => (
  <div className="py-3">
    <div className="faith-card p-6 border-2 bg-white" style={{borderColor:'#F39A1F'}}>
      <div className="text-[11px] font-overline uppercase tracking-wider-cap text-gold-deep mb-3 font-bold">Get Yours Free</div>
      <input disabled placeholder="Visitor's name" className="w-full px-4 py-3 rounded-md border border-border bg-white/80 text-base mb-2"/>
      <input disabled placeholder="visitor@email.com" className="w-full px-4 py-3 rounded-md border border-border bg-white/80 text-base mb-3"/>
      <Editable value={block.cta} onChange={t=>onChange({cta:t})} placeholder="Button text" testid={`edit-form-cta-${block.id}`}
        className="w-full text-center bg-red-wine text-white font-bold uppercase tracking-wider-cap py-3 rounded-md cursor-text"/>
      <Editable value={block.note} onChange={t=>onChange({note:t})} placeholder="Tiny note under button" testid={`edit-form-note-${block.id}`}
        className="text-[11px] tracking-wider-cap text-center text-foreground mt-2"/>
    </div>
  </div>
);


/* ============== FAITHARAMA PRODUCTS — auto-insertable into any funnel ============== */
/* Members drop these into their funnels. The PAIRED CTA below auto-injects with their
   ?ref=AFFILIATE-CODE so they earn 50% (everyone) commission on every sale. */
const FAITHARAMA_PRODUCTS = [
  {
    id: "fp-book",
    name: "Mike's Book — With Faith All Things Are Possible",
    description: "Paperback live on Books.by · $19.99 · earn 50% (everyone) per sale",
    category: "product",
    image: "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/yn41guv2_WITH%20FAITH%20ALL%20THINGS%20ARE%20POSSIBLE%20BOOK%20COVER%203D.png",
    cta_text: "Get The Paperback",
    cta_path: "https://books.by/michael-boyd",
  },
  {
    id: "fp-goli-acv",
    name: "Goli Gummies — Easy-To-Swallow Wellness",
    description: "World's first ACV gummies · Free shipping · earn 20% commission",
    category: "product",
    image: "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4jq13qjg_goli.com%20gummies.png",
    cta_text: "Get 26% Off",
    cta_path: "https://go.goli.com/g-482b22a7f8",
  },
  // NOTE — Mike is producing larger custom banners (Mike + Goli + Merch combos)
  // for the funnel templates. When ready, drop them in here as additional entries.
];

/* ============== IMAGE PICKER MODAL (with computer upload) ============== */
function ImagePickerModal({ templates, onPick, onClose }) {
  const fileRef = useRef(null);
  const [tab, setTab] = useState("library"); // 'library' | 'upload'
  const [libCat, setLibCat] = useState("all"); // 'all' | 'faith' | 'wellness' | 'business'
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("faitharama_recent_uploads") || "[]"); }
    catch { return []; }
  });

  const visibleTemplates = (() => {
    if (libCat === "products") return FAITHARAMA_PRODUCTS;
    if (libCat === "myphotos") return recent.map(url => ({ id: `my-${url.slice(-12)}`, name: "My uploaded photo", description: "", category: "personal", image: url }));
    if (libCat === "all") return [...FAITHARAMA_PRODUCTS, ...templates];
    return templates.filter(t => t.category === libCat);
  })();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      const updated = [url, ...recent.filter(u => u !== url)].slice(0, 12);
      localStorage.setItem("faitharama_recent_uploads", JSON.stringify(updated));
      setRecent(updated);
      onPick(url);
      toast.success("Image uploaded!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="image-picker">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="text-[10px] tracking-wider-cap text-gold-deep font-bold">Pick an image</div>
            <div className="text-xl uppercase text-red-wine">Choose from library or upload your own</div>
          </div>
          <button onClick={onClose} className="text-foreground hover:text-red-wine" data-testid="image-picker-close"><X className="w-6 h-6"/></button>
        </div>
        <div className="flex border-b border-border bg-white px-5">
          <button onClick={()=>setTab("library")} className={`px-4 py-3 text-[11px] tracking-wider-cap font-bold uppercase ${tab==="library"?"text-red-wine border-b-2 border-red-wine":"text-foreground"}`} data-testid="image-tab-library">Image Library</button>
          <button onClick={()=>setTab("upload")} className={`px-4 py-3 text-[11px] tracking-wider-cap font-bold uppercase ${tab==="upload"?"text-red-wine border-b-2 border-red-wine":"text-foreground"}`} data-testid="image-tab-upload">Upload from My Computer</button>
        </div>
        {tab === "library" ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-border/60 bg-white flex-wrap">
              <span className="text-[10px] tracking-wider-cap font-bold text-gold-deep uppercase mr-1">Category:</span>
              {[["all","All"],["products","★ Products"],["myphotos","My Photos"],["faith","Faith"],["wellness","Wellness"],["business","Business"]].map(([k,l]) => (
                <button
                  key={k}
                  onClick={()=>setLibCat(k)}
                  data-testid={`lib-cat-${k}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] tracking-wider-cap font-bold uppercase transition ${libCat===k ? (k==="products" ? "bg-[#F39A1F] text-[#0F172A] shadow" : "bg-red-wine text-white shadow") : "bg-white text-foreground hover:bg-white"}`}
                >{l}</button>
              ))}
              <span className="text-[10px] tracking-wider-cap font-bold text-foreground/60">{visibleTemplates.length} images</span>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={e => handleFile(e.target.files?.[0])} data-testid="image-upload-input-library"/>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="image-upload-btn-library"
                className="ml-auto bg-[#F39A1F] text-[#0F172A] hover:bg-[#E5B82B] disabled:opacity-60 px-3 py-1.5 rounded-full text-[11px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-1.5 shadow-sm transition"
              >
                <Upload className="w-3.5 h-3.5"/>{uploading ? "Uploading…" : "+ Upload My Own"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 auto-rows-max gap-4 p-5 overflow-y-auto">
              {visibleTemplates.map(t => (
                <button
                  key={t.id}
                  onClick={() => onPick(t.image, t)}
                  data-testid={`pick-${t.id}`}
                  type="button"
                  className="group block w-full text-left rounded-xl overflow-hidden border-2 border-transparent hover:border-[#F39A1F] hover:shadow-xl hover:-translate-y-0.5 transition bg-white"
                >
                  <div className="relative h-44 md:h-48 bg-foreground/10">
                    <img src={t.image} className="absolute inset-0 w-full h-full object-cover" alt={t.name} loading="lazy"/>
                    <div className={`absolute top-2 left-2 z-10 text-[9px] tracking-wider-cap font-bold uppercase px-2 py-0.5 rounded ${t.category === "product" ? "bg-[#F39A1F] text-[#0F172A]" : "bg-[#0F172A]/85 text-[#F39A1F]"}`}>
                      {t.category === "product" ? "★ Earn Commission" : t.category}
                    </div>
                  </div>
                  <div className="p-3 bg-white/60">
                    <div className="text-[12px] font-bold text-red-wine leading-tight">{t.name}</div>
                    {t.description && <div className="text-[10px] text-foreground/75 mt-0.5 line-clamp-2">{t.description}</div>}
                  </div>
                </button>
              ))}
              {visibleTemplates.length === 0 && (
                <div className="col-span-full text-center text-foreground/70 py-8 text-sm">No images in this category yet.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 overflow-y-auto">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={e => handleFile(e.target.files?.[0])} data-testid="image-upload-input"/>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-[#F39A1F]/60 hover:border-[#F39A1F] hover:bg-white rounded-xl p-12 flex flex-col items-center gap-3 text-foreground transition disabled:opacity-60"
              data-testid="image-upload-btn"
            >
              <Upload className="w-10 h-10 text-gold-deep"/>
              <div className="text-lg font-bold uppercase text-red-wine tracking-wider-cap">{uploading ? "Uploading…" : "Click to choose a photo"}</div>
              <div className="text-sm text-foreground">JPG, PNG, WEBP or GIF · up to 8 MB</div>
              <div className="text-[11px] text-foreground mt-2">Tip: square or wide photos look best on funnels</div>
            </button>
            {recent.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] tracking-wider-cap text-gold-deep font-bold mb-3">Your Recent Uploads</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {recent.map((url) => (
                    <button key={url} onClick={() => onPick(url)} className="rounded-md overflow-hidden border-2 border-transparent hover:border-[#F39A1F] transition" data-testid={`pick-recent-${url}`}>
                      <img src={url} className="w-full h-20 object-cover" alt="recent upload"/>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
